import React, { useMemo, useRef, useState } from "react";
import {
    odometerApi,
    OdometerApiError,
    OcrAvailability,
    OcrCandidate,
    OcrRecognizeResult,
    OdometerVehicleStatus,
} from "../../data/odometerApi";

interface OdometerEntryModalProps {
    vehicle: OdometerVehicleStatus;
    ocrAvailability: OcrAvailability;
    allowedExtensions: string[];
    onClose: () => void;
    onSaved: (vehicles: OdometerVehicleStatus[]) => void;
}

const todayString = (): string => {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** File → base64 (data URL 접두사 포함). 서버가 접두사를 벗겨 검증한다. */
const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("사진을 읽지 못했습니다."));
        reader.readAsDataURL(file);
    });

export default function OdometerEntryModal({
    vehicle,
    ocrAvailability,
    allowedExtensions,
    onClose,
    onSaved,
}: OdometerEntryModalProps) {
    const [measuredAt, setMeasuredAt] = useState<string>(todayString());
    const [odometerInput, setOdometerInput] = useState<string>("");
    const [memo, setMemo] = useState<string>("");

    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [ocrResult, setOcrResult] = useState<OcrRecognizeResult | null>(null);
    const [appliedCandidate, setAppliedCandidate] = useState<OcrCandidate | null>(null);

    const [isRecognizing, setIsRecognizing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [noticeMessage, setNoticeMessage] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const acceptAttribute = allowedExtensions.map((extension) => `.${extension}`).join(",");
    // 서버가 "설정됨" 이라고 답한 경우에만 인식 버튼을 연다.
    const canRecognize = ocrAvailability === "configured";
    const parsedOdometer = Number.parseInt(odometerInput.replace(/[^\d]/g, ""), 10);
    const hasOdometerValue = Number.isFinite(parsedOdometer);

    // 이전 누적거리보다 작으면 저장 전에 화면에서 먼저 막는다 (서버도 동일하게 거절한다).
    const regressionWarning = useMemo(() => {
        if (!hasOdometerValue || parsedOdometer >= vehicle.previousKm) {
            return null;
        }
        return `이전 누적거리 ${vehicle.previousKm.toLocaleString()} km 보다 작습니다. 계기판 값을 다시 확인해 주세요.`;
    }, [hasOdometerValue, parsedOdometer, vehicle.previousKm]);

    const canSave = hasOdometerValue && !regressionWarning && !isSaving && Boolean(measuredAt);

    const applyCandidate = (candidate: OcrCandidate) => {
        setOdometerInput(String(candidate.odometerKm));
        setAppliedCandidate(candidate);
    };

    const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        setErrorMessage(null);
        setNoticeMessage(null);
        setOcrResult(null);
        setAppliedCandidate(null);
        setPhotoFile(file);

        if (!file) {
            setPhotoPreview(null);
            return;
        }
        try {
            setPhotoPreview(await readFileAsDataUrl(file));
        } catch {
            setPhotoPreview(null);
            setErrorMessage("사진 미리보기를 만들지 못했습니다. 다른 파일을 선택해 주세요.");
        }
    };

    const handleRecognize = async () => {
        if (!photoFile || !photoPreview) {
            setErrorMessage("먼저 계기판 사진을 선택해 주세요.");
            return;
        }
        setIsRecognizing(true);
        setErrorMessage(null);
        setNoticeMessage(null);
        try {
            const result = await odometerApi.recognizePhoto({
                fileName: photoFile.name,
                data: photoPreview,
            });
            setOcrResult(result);

            if (result.unavailableReason) {
                setNoticeMessage(`${result.unavailableReason} 주행거리를 직접 입력해 주세요.`);
                return;
            }
            if (!result.bestCandidate) {
                setNoticeMessage("사진에서 주행거리로 볼 만한 숫자를 찾지 못했습니다. 직접 입력해 주세요.");
                return;
            }
            // 인식값은 입력창을 "채우기"만 한다. 저장은 사용자가 확인 후 직접 눌러야 한다.
            applyCandidate(result.bestCandidate);
            if (result.lowConfidence) {
                const percent = Math.round(result.bestCandidate.confidence * 100);
                setNoticeMessage(`인식 신뢰도가 낮습니다(${percent}%). 값이 맞는지 확인 후 저장해 주세요.`);
            }
        } catch (error) {
            const message =
                error instanceof OdometerApiError ? error.message : "OCR 인식 중 오류가 발생했습니다.";
            setErrorMessage(`${message} 주행거리를 직접 입력할 수 있습니다.`);
        } finally {
            setIsRecognizing(false);
        }
    };

    const clearPhoto = () => {
        setPhotoFile(null);
        setPhotoPreview(null);
        setOcrResult(null);
        setAppliedCandidate(null);
        setNoticeMessage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSave = async () => {
        if (!canSave) {
            return;
        }
        setIsSaving(true);
        setErrorMessage(null);
        try {
            const response = await odometerApi.createReading({
                vin: vehicle.vin,
                odometerKm: parsedOdometer,
                measuredAt,
                inputMethod: appliedCandidate ? "OCR_ASSISTED" : "MANUAL",
                memo: memo.trim() || undefined,
                photo: ocrResult?.photo ?? null,
                ocr: appliedCandidate
                    ? {
                          candidateKm: appliedCandidate.odometerKm,
                          confidence: appliedCandidate.confidence,
                          requestId: ocrResult?.requestId ?? null,
                          rawText: ocrResult?.recognizedText ?? null,
                      }
                    : null,
            });
            onSaved(response.vehicles);
        } catch (error) {
            setErrorMessage(
                error instanceof OdometerApiError ? error.message : "저장 중 오류가 발생했습니다."
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="odo-modal-backdrop" onClick={onClose}>
            <div className="odo-modal" onClick={(event) => event.stopPropagation()}>
                <div className="odo-modal-header">
                    <div>
                        <h2 className="odo-modal-title">주행거리 입력</h2>
                        <div className="odo-modal-subtitle">
                            {vehicle.vehicleNumber}
                            {vehicle.model ? ` · ${vehicle.model}` : ""}
                            {vehicle.depot ? ` · ${vehicle.depot}` : ""}
                        </div>
                    </div>
                    <button type="button" className="odo-close-btn" onClick={onClose} aria-label="닫기">
                        &times;
                    </button>
                </div>

                <div className="odo-modal-body">
                    <div className="odo-prev-strip">
                        <span className="odo-prev-label">직전 누적거리</span>
                        <span className="odo-prev-value">{vehicle.previousKm.toLocaleString()} km</span>
                        <span className="odo-prev-meta">
                            {vehicle.latestMeasuredAt
                                ? `${vehicle.latestMeasuredAt} 입력`
                                : "입력 이력 없음 (차량 등록값 기준)"}
                        </span>
                    </div>

                    {/* 1. 사진 첨부 — 선택 사항. 없이도 저장할 수 있다. */}
                    <section className="odo-section">
                        <div className="odo-section-title">
                            계기판 사진 <span className="odo-optional">선택</span>
                        </div>
                        <div className="odo-photo-row">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={acceptAttribute}
                                onChange={handlePhotoChange}
                                className="odo-file-input"
                            />
                            {photoFile && (
                                <button type="button" className="odo-btn ghost" onClick={clearPhoto}>
                                    사진 제거
                                </button>
                            )}
                        </div>
                        <div className="odo-hint">
                            {allowedExtensions.join(", ").toUpperCase()} 형식만 지원합니다. 사진 원본은 저장하지
                            않고 인식에만 사용합니다.
                        </div>

                        {photoPreview && (
                            <div className="odo-photo-preview-wrap">
                                <img src={photoPreview} alt="계기판 미리보기" className="odo-photo-preview" />
                                <button
                                    type="button"
                                    className="odo-btn primary"
                                    onClick={handleRecognize}
                                    disabled={isRecognizing || !canRecognize}
                                    title={
                                        canRecognize
                                            ? undefined
                                            : ocrAvailability === "unconfigured"
                                              ? "서버에 CLOVA OCR 설정이 없습니다."
                                              : "데모 API 서버에 연결하지 못했습니다."
                                    }
                                >
                                    {isRecognizing ? "인식 중..." : "사진에서 주행거리 인식"}
                                </button>
                            </div>
                        )}
                        {ocrAvailability === "unconfigured" && (
                            <div className="odo-banner warn">
                                서버에 CLOVA OCR이 설정되지 않아 사진 인식을 사용할 수 없습니다. 주행거리를 직접
                                입력해 주세요.
                            </div>
                        )}
                        {ocrAvailability === "unknown" && (
                            <div className="odo-banner warn">
                                데모 API 서버에 연결하지 못해 사진 인식을 사용할 수 없습니다. 주행거리를 직접 입력해
                                주세요.
                            </div>
                        )}
                    </section>

                    {/* 2. 인식 후보 — 클릭하면 입력창을 채운다. 자동 저장하지 않는다. */}
                    {ocrResult && ocrResult.candidates.length > 0 && (
                        <section className="odo-section">
                            <div className="odo-section-title">
                                인식 결과 <span className="odo-optional">클릭하면 입력창에 채워집니다</span>
                            </div>
                            <div className="odo-candidate-list">
                                {ocrResult.candidates.map((candidate) => (
                                    <button
                                        type="button"
                                        key={`${candidate.odometerKm}-${candidate.rawText}`}
                                        className={`odo-candidate ${
                                            appliedCandidate?.odometerKm === candidate.odometerKm ? "selected" : ""
                                        }`}
                                        onClick={() => applyCandidate(candidate)}
                                    >
                                        <span className="odo-candidate-km">
                                            {candidate.odometerKm.toLocaleString()} km
                                        </span>
                                        <span className="odo-candidate-meta">
                                            신뢰도 {Math.round(candidate.confidence * 100)}%
                                            {candidate.matchedKeyword ? ` · ${candidate.matchedKeyword}` : ""}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {ocrResult.recognizedText && (
                                <div className="odo-recognized-text">인식 텍스트: {ocrResult.recognizedText}</div>
                            )}
                        </section>
                    )}

                    {/* 3. 최종 입력 — 사용자가 수정·확인 후 저장 */}
                    <section className="odo-section">
                        <div className="odo-field-grid">
                            <div className="odo-field">
                                <label htmlFor="odo-measured-at">측정일</label>
                                <input
                                    id="odo-measured-at"
                                    type="date"
                                    value={measuredAt}
                                    max={todayString()}
                                    onChange={(event) => setMeasuredAt(event.target.value)}
                                />
                            </div>
                            <div className="odo-field">
                                <label htmlFor="odo-km">누적 주행거리 (km)</label>
                                <input
                                    id="odo-km"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="예: 128430"
                                    value={odometerInput}
                                    onChange={(event) => setOdometerInput(event.target.value)}
                                    className={regressionWarning ? "invalid" : ""}
                                />
                            </div>
                        </div>
                        <div className="odo-field">
                            <label htmlFor="odo-memo">메모</label>
                            <input
                                id="odo-memo"
                                type="text"
                                placeholder="선택 입력"
                                value={memo}
                                onChange={(event) => setMemo(event.target.value)}
                            />
                        </div>
                        <div className="odo-hint">
                            {appliedCandidate
                                ? "OCR 인식값이 채워졌습니다. 실제 계기판과 다르면 직접 수정하세요."
                                : "사진 없이 직접 입력해도 됩니다."}
                        </div>
                    </section>

                    {regressionWarning && <div className="odo-banner error">{regressionWarning}</div>}
                    {noticeMessage && <div className="odo-banner warn">{noticeMessage}</div>}
                    {errorMessage && <div className="odo-banner error">{errorMessage}</div>}
                </div>

                <div className="odo-modal-footer">
                    <button type="button" className="odo-btn ghost" onClick={onClose} disabled={isSaving}>
                        취소
                    </button>
                    <button type="button" className="odo-btn primary" onClick={handleSave} disabled={!canSave}>
                        {isSaving ? "저장 중..." : "확인 후 저장"}
                    </button>
                </div>
            </div>
        </div>
    );
}
